import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Layers, Film, Wrench, Clock,
  Download, FileJson, FileText, Music, Waypoints, Gauge, Library, Trash2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SRC_CLASS_LABEL = { lossless: "LOSSLESS", lossy: "LOSSY", unknown: "MIXED/UNKNOWN" };
const SRC_CLASS_BADGE = {
  lossless: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  lossy: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  unknown: "bg-slate-600/25 text-slate-300 ring-slate-500/30",
};

function Metric({ label, value, tone }) {
  return (
    <div className="rounded-md border border-border bg-[#0d1016] px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm ${tone === "amber" ? "text-amber-300" : "text-slate-200"}`}>{value ?? "—"}</div>
    </div>
  );
}

function StateBadge({ ok, label, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ${ok ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30" : "bg-slate-700/30 text-slate-400 ring-slate-600/40"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {label}
    </span>
  );
}

function SummaryChip({ label, count, nums, tone, testid }) {
  const toneCls = { emerald: "text-emerald-300", amber: "text-amber-300", slate: "text-slate-300", red: "text-red-300" }[tone] || "text-slate-300";
  return (
    <div className="rounded-md border border-border bg-[#0d1016] px-2.5 py-1.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</span>
        <span className={`font-mono text-sm ${toneCls}`}>{count}</span>
      </div>
      {nums?.length > 0 && <div className="mt-0.5 truncate font-mono text-[10px] text-slate-500" title={nums.join(", ")}>#{nums.join(" #")}</div>}
    </div>
  );
}

function PassageRow({ p }) {
  return (
    <div data-testid={`assembly-passage-${p.index}`} className={`rounded-lg border p-3 ${p.ready ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/25 bg-red-500/5"}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-200">
          <span className="font-mono text-slate-500">#{p.index + 1}</span> · ¶{p.start_body}–{p.end_body}
          <span className="ml-2 text-slate-500">scene {(p.scene_index ?? 0) + 1}</span>
        </span>
        <span className="flex items-center gap-2">
          {p.active_take_stitch_ready === false && p.active_take_id && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><AlertTriangle className="h-3 w-3" /> alignment unavailable</span>
          )}
          {p.duration != null
            ? <span className="font-mono text-slate-400">{p.duration.toFixed(2)}s</span>
            : <span className="font-mono text-slate-500">~{Math.round((p.char_count || 0) / 14.5)}s est</span>}
          {p.ready
            ? <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> ready</span>
            : <span className="inline-flex items-center gap-1 text-red-300"><AlertTriangle className="h-3.5 w-3.5" /> blocked</span>}
        </span>
      </div>
      {p.active_take_id && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px]" data-testid={`assembly-passage-format-${p.index}`}>
          <span className={`rounded px-1.5 py-0.5 uppercase tracking-wider ring-1 ${SRC_CLASS_BADGE[p.source_class] || SRC_CLASS_BADGE.unknown}`}>{SRC_CLASS_LABEL[p.source_class] || "MIXED/UNKNOWN"}</span>
          <span className="text-slate-500">requested: <span className="text-slate-300">{p.requested_format || "—"}</span></span>
          <span className="text-slate-500">actual: <span className="text-slate-300">{p.actual_format || "—"}</span></span>
          {p.media_valid === true && <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" /> playback ok</span>}
          {p.media_valid === false && <span className="inline-flex items-center gap-1 text-red-300"><AlertTriangle className="h-3 w-3" /> playback failed</span>}
          {p.media_valid == null && <span className="text-slate-500">playback unverified</span>}
        </div>
      )}
      {p.placed_repairs?.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {p.placed_repairs.map((r) => (
            <div key={r.id} className="flex items-center gap-1.5 text-[11px] text-amber-300/90">
              <Wrench className="h-3 w-3" />
              {(r.state === "placeable" || r.state === "attached" || r.state === "rebased")
                ? <span>repair placed{r.state === "rebased" ? " (re-mapped to active take)" : ""} @ {r.start_seconds?.toFixed(2)}–{r.end_seconds?.toFixed(2)}s · “{(r.repair_text || "").slice(0, 40)}”</span>
                : <span className="text-red-300">{r.reason}</span>}
            </div>
          ))}
        </div>
      )}
      {p.blockers?.length > 0 && (
        <div className="mt-1 text-[11px] text-red-300/90">{p.blockers.join(" · ")}</div>
      )}
    </div>
  );
}

function CueSheet({ cue }) {
  if (!cue) return null;
  return (
    <div className="rounded-md border border-border bg-[#0d1016] p-3" data-testid="cue-sheet">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Cue sheet · {cue.chapter}</span>
        <span className="font-mono text-xs text-amber-300">{cue.total_timecode}{cue.durations_estimated ? " (est)" : ""}</span>
      </div>
      <div className="max-h-56 space-y-0.5 overflow-y-auto font-mono text-[11px]">
        {cue.markers.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.type === "chapter" ? "text-amber-200" : m.type === "scene" ? "text-sky-300" : m.type === "repair" ? "text-amber-400/90" : "text-slate-300"}`}>
            <span className="w-24 shrink-0 text-slate-500">{m.start_timecode}</span>
            <span className="w-16 shrink-0 uppercase tracking-wider text-slate-600">{m.type}</span>
            <span className="truncate">{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExportDialog({ projectId, mode, chapterOrder, chapterId, range, settings, onClose, onDone }) {
  const [preflight, setPreflight] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(null); // {phase, done, total}

  useEffect(() => {
    const body = { mode, chapter_order: chapterOrder, chapter_id: chapterId, settings };
    if (mode === "preview" && range) { body.start_index = range.start_index; body.end_index = range.end_index; }
    api.post(`/projects/${projectId}/assembly/export`, body)
      .then(({ data }) => setPreflight(data))
      .catch((e) => setErr(formatApiError(e?.response?.data?.detail)));
  }, [projectId, mode, chapterOrder, chapterId, range, settings]);

  const render = async () => {
    if (!window.confirm(`Render an immutable ${mode.toUpperCase()} export version now? This uses FFmpeg only (no ElevenLabs / no paid call) and never alters your source takes or repair clips.`)) return;
    setBusy(true);
    try {
      const body = { mode, chapter_order: chapterOrder, chapter_id: chapterId, settings, confirm: true };
      if (mode === "preview" && range) { body.start_index = range.start_index; body.end_index = range.end_index; }
      const { data } = await api.post(`/projects/${projectId}/assembly/export`, body);
      if (!data.job_id) { // legacy synchronous fallback
        toast.success("Export version rendered.");
        if (onDone) await onDone();
        onClose();
        return;
      }
      // Long renders run as a background job — poll to completion so a big chapter
      // never blocks the request past the ingress timeout.
      setProgress({ phase: "queued", done: 0, total: data.passage_count || 0 });
      const jobId = data.job_id;
      await new Promise((resolve) => {
        const iv = setInterval(async () => {
          try {
            const { data: j } = await api.get(`/projects/${projectId}/assembly/chapter-jobs/${jobId}`);
            setProgress({ phase: j.phase, done: j.done_passages || 0, total: j.total_passages || 0 });
            if (j.status !== "running") {
              clearInterval(iv);
              if (j.status === "completed") {
                toast.success("Export version rendered.");
                if (onDone) await onDone();
                onClose();
              } else {
                toast.error(j.error || `Render ${j.status}.`);
                setErr(j.error || `Render ${j.status}.`);
                setBusy(false); setProgress(null);
              }
              resolve();
            }
          } catch { /* keep polling */ }
        }, 2000);
      });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-border bg-[#0d1016] text-slate-200 max-h-[90vh] overflow-y-auto" data-testid="export-preflight">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-slate-100">
            {mode === "final" ? <Film className="h-5 w-5 text-emerald-400" /> : <Layers className="h-5 w-5 text-amber-400" />}
            {mode === "final" ? "Final Chapter Export" : mode === "draft" ? "Draft Render (ready passages)" : "Preview Range Export"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === "final"
              ? "Every chapter passage has an approved take and all placed repairs are stitch-ready."
              : mode === "draft"
              ? "Stitches every approved passage so far (skipping any not-yet-generated ones), labelled DRAFT · INCOMPLETE. Not blocked by the chapter heading."
              : "A contiguous run of approved passages — labelled PREVIEW · INCOMPLETE CHAPTER. Ranges never span gaps."}
          </DialogDescription>
        </DialogHeader>
        {err && <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="export-error">{err}</div>}
        {!preflight && !err && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>}
        {preflight && (
          <div className="space-y-4">
            {(mode === "preview" || mode === "draft") && (
              <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-300" data-testid="preview-warning">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {mode === "draft" ? "Draft — incomplete chapter (ready passages only)" : "Preview — incomplete chapter"}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric label="Passages" value={preflight.passage_count} />
              <Metric label="Sample rate" value={`${preflight.settings.sample_rate} Hz`} />
              <Metric label="WAV depth" value={`${preflight.settings.wav_bit_depth}-bit`} tone="amber" />
              <Metric label="MP3" value={preflight.settings.mp3_bitrate} />
            </div>
            <CueSheet cue={preflight.cue_sheet} />
            <div className="rounded-md border border-slate-600/40 bg-[#12161f] p-2 text-[11px] text-slate-400">
              Outputs: WAV master · MP3 review copy · JSON cue sheet · text cue sheet. Crossfade {preflight.settings.crossfade_ms}ms at repair seams. This is a NEW immutable export version — sources untouched. Source-take quality is recorded per passage on the export card.
            </div>
            <Button data-testid="export-confirm-render" onClick={render} disabled={busy} className="w-full gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {busy && progress
                ? `Rendering… ${progress.phase || ""}${progress.total ? ` · ${progress.done}/${progress.total} passages` : ""}`
                : "Confirm & render export version (FFmpeg · no paid call)"}
            </Button>
            {busy && progress && (
              <p className="text-center text-[11px] text-slate-500" data-testid="export-progress">
                Large chapters can take a minute or two — you can keep this open; it will finish in the background.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SRC_CLASS_STYLE = {
  LOSSLESS_PRODUCTION: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  LOSSY_PRODUCTION: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  AUDITION: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  INVALID: "bg-red-500/15 text-red-300 ring-red-500/30",
};
const SRC_QUALITY_STYLE = {
  lossless: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  lossy: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  audition: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  invalid: "bg-red-500/15 text-red-300 ring-red-500/30",
  unknown: "bg-slate-600/25 text-slate-300 ring-slate-500/30",
};

function ExportCard({ projectId, e, isLatestBook = false, missingSections = [], displayNumber = null, deletable = false, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const t = e.tech || {};
  const sq = e.source_quality || null;
  const sources = e.source_takes || [];
  const outdated = e.mode === "book" && missingSections.length > 0;
  const del = async () => {
    if (!window.confirm(`Delete export "${e.title}" (${new Date(e.created_at).toLocaleDateString()})? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${projectId}/assembly/exports/${e.id}`);
      toast.success("Export deleted.");
      if (onDeleted) await onDeleted();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setDeleting(false); }
  };
  const specLine = (s) => {
    const bits = [s.requested_format, s.codec,
      s.sample_rate ? `${s.sample_rate}Hz` : null,
      s.bit_depth ? `${s.bit_depth}-bit` : (s.bitrate_kbps ? `${s.bitrate_kbps}kbps` : null),
      s.channels != null ? (s.channels === 1 ? "mono" : s.channels === 2 ? "stereo" : `${s.channels}ch`) : null,
      s.duration != null ? `${Number(s.duration).toFixed(2)}s` : null,
    ].filter(Boolean);
    return bits.join(" · ");
  };
  return (
    <div className={`rounded-lg border p-3 ${isLatestBook ? "border-teal-500/40 bg-teal-500/[0.06]" : "border-border bg-[#12161f]"}`} data-testid={`export-${e.id}`}>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="flex flex-wrap items-center gap-1.5 text-slate-200">
          <span className="font-mono text-slate-500">v{displayNumber ?? e.export_number}</span> · {e.title}
          <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${e.mode === "final" ? "bg-emerald-500/15 text-emerald-300" : e.mode === "book" ? "bg-teal-500/15 text-teal-300" : "bg-amber-500/15 text-amber-300"}`}>{e.mode}</span>
          {isLatestBook && <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-teal-500/20 text-teal-200 ring-1 ring-teal-500/40" data-testid={`export-latest-${e.id}`}>latest</span>}
          {outdated && <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30" data-testid={`export-outdated-${e.id}`}>outdated</span>}
          {sq && <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ring-1 ${SRC_QUALITY_STYLE[sq.code] || SRC_QUALITY_STYLE.unknown}`} data-testid={`export-source-quality-${e.id}`}>{sq.label}</span>}
        </span>
        <span className="flex items-center gap-2">
          <span className="font-mono text-slate-500">{new Date(e.created_at).toLocaleString()}</span>
          {deletable && (
            <button data-testid={`export-delete-${e.id}`} onClick={del} disabled={deleting}
              title="Delete this export" className="text-slate-500 transition-colors hover:text-red-400 disabled:opacity-40">
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
        </span>
      </div>
      {outdated && (
        <div className="mb-2 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300" data-testid={`export-outdated-note-${e.id}`}>
          <AlertTriangle className="mr-1 inline h-3 w-3" /> Newer approved sections exist since this render — re-run “Batch export all chapters” to include: {missingSections.join(", ")}.
        </div>
      )}
      <div className="my-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Duration" value={t.duration_seconds != null ? `${t.duration_seconds.toFixed(2)}s` : "—"} />
        <Metric label="Peak" value={t.peak_dbfs != null ? `${t.peak_dbfs} dBFS` : "—"} />
        <Metric label="Loudness" value={t.integrated_lufs != null ? `${t.integrated_lufs} LUFS` : "—"} tone="amber" />
        <Metric label="Channels" value={t.wav?.channels} />
      </div>
      <audio controls preload="none" className="h-8 w-full" src={`${BACKEND}${e.artifacts.mp3}`} data-testid={`export-audio-${e.id}`} />

      {e.sections?.length > 0 && (
        <Accordion type="single" collapsible className="mt-2" data-testid={`export-sections-acc-${e.id}`}>
          <AccordionItem value="sections" className="overflow-hidden rounded-md border border-teal-500/20 bg-teal-500/[0.04]">
            <AccordionTrigger data-testid={`export-sections-${e.id}`} className="px-2 py-1.5 hover:no-underline">
              <span className="font-mono text-[9px] uppercase tracking-wider text-teal-300/70">Book sections ({e.sections.length})</span>
            </AccordionTrigger>
            <AccordionContent className="px-2 pb-2">
              <div className="flex flex-wrap gap-1.5">
                {e.sections.map((s, i) => (
                  <span key={i} className={`rounded px-1.5 py-0.5 text-[10px] ${s.final_ready ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>
                    {s.title} · {Math.round(s.duration_seconds)}s{s.final_ready ? "" : " (draft)"}
                  </span>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <Accordion type="single" collapsible className="mt-2" data-testid={`export-source-takes-acc-${e.id}`}>
        <AccordionItem value="takes" className="overflow-hidden rounded-md border border-border bg-[#0d1016]">
          <AccordionTrigger data-testid={`export-source-takes-${e.id}`} className="px-2 py-1.5 hover:no-underline">
            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">Source Takes ({sources.length})</span>
          </AccordionTrigger>
          <AccordionContent className="px-2 pb-2">
            {sources.length === 0
              ? <div className="text-[11px] text-slate-500">Not recorded for this export version.</div>
              : (
                <div className="max-h-80 space-y-1 overflow-y-auto">
                  {sources.map((s) => (
                    <div key={s.passage_index} className="flex flex-col gap-0.5 border-b border-border/50 pb-1 last:border-0 last:pb-0" data-testid={`export-source-${e.id}-${s.passage_index}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-300">{s.passage_label}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider ring-1 ${SRC_CLASS_STYLE[s.classification] || SRC_CLASS_STYLE.INVALID}`}>{s.classification_label}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 font-mono text-[10px] text-slate-500">
                        <span>{specLine(s)}</span>
                        <span className="whitespace-nowrap">take {s.take_id ? s.take_id.slice(-6) : "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {e.source_quality_warning && (
        <div className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300" data-testid={`export-quality-warning-${e.id}`}>
          <AlertTriangle className="mr-1 inline h-3 w-3" /> {e.source_quality_warning}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <a data-testid={`export-download-wav-${e.id}`} href={`${BACKEND}${e.artifacts.wav}`} className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] hover:border-amber-500/40 ${sq && !sq.all_lossless ? "border-amber-500/40 text-amber-300" : "border-border text-slate-300"}`}><Music className="h-3 w-3" /> {sq ? sq.wav_label : "WAV master"}</a>
        <a data-testid={`export-download-mp3-${e.id}`} href={`${BACKEND}${e.artifacts.mp3}`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-slate-300 hover:border-amber-500/40"><Download className="h-3 w-3" /> MP3</a>
        <a data-testid={`export-download-cue-json-${e.id}`} href={`${BACKEND}${e.artifacts.cue_json}`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-slate-300 hover:border-amber-500/40"><FileJson className="h-3 w-3" /> Cue JSON</a>
        <a data-testid={`export-download-cue-txt-${e.id}`} href={`${BACKEND}${e.artifacts.cue_txt}`} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-slate-300 hover:border-amber-500/40"><FileText className="h-3 w-3" /> Cue text</a>
      </div>
    </div>
  );
}

const KIND_BADGE = {
  prologue: "bg-purple-500/15 text-purple-300 ring-purple-500/30",
  epilogue: "bg-purple-500/15 text-purple-300 ring-purple-500/30",
  chapter: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
};

function ChapterSection({ projectId, chap, settings, exports, ffmpegAvailable, onDone, embedded }) {
  const [exportCfg, setExportCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const notReady = chap.passages.filter((p) => !p.ready).length;
  const anyReady = chap.passages.some((p) => p.ready);
  const heading = chap.heading;
  const headingBlocks = heading?.enabled && heading.status !== "approved";

  const approveHeading = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/chapters/${chap.chapter_id}/heading/approve`);
      toast.success("Chapter heading approved — final export unblocked.");
      if (onDone) await onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const generateHeading = async () => {
    if (!window.confirm(`Generate the spoken "${heading?.text}" heading with Scott? This makes one billed ElevenLabs call.`)) return;
    setBusy(true);
    try {
      const f = new FormData(); f.append("confirm", "true");
      await api.post(`/projects/${projectId}/chapters/${chap.chapter_id}/heading/generate`, f, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Heading take generated — approve it to unblock final export.");
      if (onDone) await onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={embedded ? "p-4 sm:p-5" : "rounded-xl border border-border/70 bg-[#0b0e14] p-4 sm:p-5"} data-testid={`assembly-chapter-${chap.chapter_id}`}>
      {/* Readiness banner */}
      <div className={`rounded-lg border p-4 ${chap.final_ready ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="assembly-readiness">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${KIND_BADGE[chap.kind] || KIND_BADGE.chapter}`}>{chap.kind || "chapter"}</span>
            <span className="font-serif text-lg text-slate-100">{chap.title}</span>
          </div>
          {chap.final_ready
            ? <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Final export ready</span>
            : <span className="inline-flex items-center gap-1.5 text-sm text-amber-300"><AlertTriangle className="h-4 w-4" /> {chap.passages.length === 0 ? "No passages planned" : `Incomplete — ${notReady} of ${chap.passages.length} passages not ready`}</span>}
        </div>
        {chap.readiness && chap.passages.length > 0 && (
          <>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="assembly-readiness-states">
              <StateBadge ok={chap.readiness.complete} label="Complete" testid="state-complete" />
              <StateBadge ok={chap.readiness.lossless_master_ready} label="Lossless master ready" testid="state-lossless" />
              <StateBadge ok={chap.readiness.playback_verified} label="Playback verified" testid="state-playback" />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="assembly-source-summary">
              <SummaryChip label="Lossless" count={chap.readiness.counts.lossless} nums={chap.readiness.lossless_passages} tone="emerald" testid="summary-lossless" />
              <SummaryChip label="Lossy" count={chap.readiness.counts.lossy} nums={chap.readiness.lossy_passages} tone="amber" testid="summary-lossy" />
              <SummaryChip label="Mixed/Unknown" count={chap.readiness.counts.mixed_unknown} nums={chap.readiness.mixed_unknown_passages} tone="slate" testid="summary-unknown" />
              <SummaryChip label="Playback failed" count={chap.readiness.counts.playback_failed} nums={chap.readiness.playback_failed_passages} tone="red" testid="summary-playback-failed" />
            </div>
          </>
        )}
        {!chap.final_ready && chap.final_blockers?.length > 0 && (
          <ul className="mt-2 list-inside list-disc text-xs text-amber-300/90" data-testid="final-blockers">
            {chap.final_blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        {headingBlocks && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2" data-testid="heading-action">
            <span className="text-xs text-amber-200">
              Narrated heading “{heading.text}” — {heading.status === "generated" ? "generated, needs approval to allow the Final export." : "not generated yet (Final export needs it, or turn headings off)."}
            </span>
            {heading.audio_url && (
              <audio controls preload="none" className="h-8" src={`${BACKEND}${heading.audio_url}`} data-testid={`assembly-heading-audio-${chap.chapter_id}`} />
            )}
            {heading.status === "generated" ? (
              <Button size="sm" data-testid="approve-heading-btn" disabled={busy} onClick={approveHeading}
                className="h-7 gap-1 bg-emerald-500 text-slate-950 text-xs hover:bg-emerald-400">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Approve heading
              </Button>
            ) : (
              <Button size="sm" data-testid="generate-heading-btn" disabled={busy} onClick={generateHeading}
                className="h-7 gap-1 bg-amber-500 text-slate-950 text-xs hover:bg-amber-400">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Music className="h-3 w-3" />} Generate heading (paid)
              </Button>
            )}
          </div>
        )}
        {chap.source_format_warnings?.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="source-format-warnings">
            {chap.source_format_warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-amber-300/90"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}</div>
            ))}
          </div>
        )}
      </div>

      {/* Export actions */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button data-testid="export-final-btn" disabled={!chap.final_ready || !ffmpegAvailable}
          onClick={() => setExportCfg({ mode: "final" })}
          className="gap-2 bg-emerald-500 text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
          <Film className="h-4 w-4" /> Final {chap.kind === "chapter" ? "Chapter" : "Section"} Export
        </Button>
        {!chap.final_ready && anyReady && (
          <Button data-testid="export-draft-btn" disabled={!ffmpegAvailable}
            onClick={() => setExportCfg({ mode: "draft" })}
            className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-50">
            <Layers className="h-4 w-4" /> Draft render ({chap.passages.length - notReady} ready)
          </Button>
        )}
        {chap.preview_ranges?.length > 0 && (
          <div className="flex items-center gap-2">
            <Select onValueChange={(v) => {
              const r = chap.preview_ranges.find((x) => `${x.start_index}-${x.end_index}` === v);
              if (r) setExportCfg({ mode: "preview", range: r });
            }}>
              <SelectTrigger data-testid="preview-range-select" className="w-64 bg-[#0d1016]"><SelectValue placeholder="Preview a ready range…" /></SelectTrigger>
              <SelectContent>
                {chap.preview_ranges.map((r) => (
                  <SelectItem key={`${r.start_index}-${r.end_index}`} value={`${r.start_index}-${r.end_index}`}>
                    ¶ passages #{r.start_index + 1}–#{r.end_index + 1} ({r.passage_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-[11px] text-slate-500">contiguous approved run · no gaps</span>
          </div>
        )}
      </div>

      {/* Cue sheet preview */}
      <div className="mt-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Clock className="h-3 w-3" /> Timeline preview</div>
      <div className="mt-2"><CueSheet cue={chap.cue_sheet_preview} /></div>

      {/* Timeline */}
      <div className="mt-4">
        <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Waypoints className="h-3 w-3" /> Passage assembly timeline ({chap.passages.length})</div>
        <div className="space-y-1.5">
          {chap.passages.map((p) => <PassageRow key={p.id} p={p} />)}
        </div>
      </div>

      {/* Per-chapter exports */}
      {exports?.length > 0 && (
        <div className="mt-4" data-testid="exports-list">
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Download className="h-3 w-3" /> Immutable export versions ({exports.length})</div>
          <div className="space-y-2">{exports.map((e) => <ExportCard key={e.id} projectId={projectId} e={e} />)}</div>
        </div>
      )}

      {exportCfg && (
        <ExportDialog projectId={projectId} mode={exportCfg.mode} chapterOrder={chap.chapter_order} chapterId={chap.chapter_id}
          range={exportCfg.range} settings={settings} onClose={() => setExportCfg(null)} onDone={onDone} />
      )}
    </div>
  );
}

function BookExportPanel({ projectId, ffmpegAvailable, onDone, initialJob, chapters = [] }) {
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [job, setJob] = useState(initialJob || null);
  const [openSections, setOpenSections] = useState([]);
  const pollRef = useRef(null);

  useEffect(() => { setJob(initialJob || null); }, [initialJob]);

  useEffect(() => {
    if (!job || job.status !== "running") { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/projects/${projectId}/assembly/book-jobs/${job.id}`);
        setJob(j);
        if (j.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (j.status === "completed") toast.success("Book rendered — see Whole-book exports below.");
          else toast.error(`Book render failed: ${j.error || "unknown error"}`);
          if (onDone) await onDone();
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [job, projectId, onDone]);

  const review = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/assembly/export-book`, {});
      setPlan(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const render = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/assembly/export-book`, { confirm: true });
      setPlan(null);
      toast.message(`Book render started — ${data.included_sections} section(s). This can take a few minutes.`);
      setJob({ id: data.job_id, status: "running", total_sections: data.included_sections, done_sections: 0, current: null });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const running = job && job.status === "running";

  return (
    <div className="rounded-lg border border-teal-500/25 bg-teal-500/[0.05] p-4" data-testid="book-export-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-teal-300/80"><Library className="h-3 w-3" /> Whole-book export</div>
          <p className="mt-1 text-xs text-slate-400">Stitch every section that has approved audio into ONE combined file with a book-level cue sheet. Incomplete sections are skipped and it&apos;s labelled DRAFT. Per-chapter exports below stay available.</p>
        </div>
        <Button data-testid="batch-export-book-btn" disabled={busy || running || !ffmpegAvailable} onClick={review}
          className="gap-2 bg-teal-500 text-slate-950 hover:bg-teal-400 disabled:opacity-50">
          {busy || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Library className="h-4 w-4" />} Batch export all chapters
        </Button>
      </div>

      {chapters.length > 0 && (() => {
        const includedCount = chapters.filter((c) => c.passages.some((p) => p.ready)).length;
        const includedPassages = chapters.reduce((n, c) => n + c.passages.filter((p) => p.ready).length, 0);
        return (
          <div className="mt-3" data-testid="book-build-sections">
            <div className="mb-1.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-teal-300/70">
              <span>Sections in this build</span>
              <span className="text-slate-500">{includedCount} of {chapters.length} · {includedPassages} passages</span>
            </div>
            <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="space-y-1.5" data-testid="book-build-accordion">
              {chapters.map((c) => {
                const readyCount = c.passages.filter((p) => p.ready).length;
                const notReady = c.passages.filter((p) => !p.ready);
                const included = readyCount > 0;
                const full = readyCount === c.passages.length && c.passages.length > 0;
                const val = c.chapter_id || String(c.chapter_order);
                return (
                  <AccordionItem key={val} value={val} data-testid={`book-build-item-${c.chapter_id}`}
                    className={`overflow-hidden rounded-lg border ${included ? "border-teal-500/25 bg-teal-500/[0.04]" : "border-border bg-[#0d1016]"}`}>
                    <AccordionTrigger data-testid={`book-build-trigger-${c.chapter_id}`} className="px-3 py-2 hover:no-underline">
                      <div className="flex flex-1 items-center justify-between gap-3 pr-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 ${KIND_BADGE[c.kind] || KIND_BADGE.chapter}`}>{c.kind || "chapter"}</span>
                          <span className="truncate text-sm text-slate-100">{c.title}</span>
                        </div>
                        <span className={`whitespace-nowrap font-mono text-[11px] ${!included ? "text-slate-600" : full ? "text-emerald-300" : "text-amber-300"}`}>
                          {c.passages.length === 0 ? "no passages" : included ? `${readyCount}/${c.passages.length} ready · ${full ? "included" : "partial"}` : "skipped (none ready)"}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent data-testid={`book-build-content-${c.chapter_id}`} className="px-3 pb-3">
                      {c.passages.length === 0
                        ? <div className="text-[11px] text-slate-500">No passages planned in this section yet.</div>
                        : included
                          ? (notReady.length === 0
                              ? <div className="text-[11px] text-emerald-300/80">All {readyCount} passages ready — this whole section will be in the book.</div>
                              : <div className="text-[11px] text-amber-300/80">{readyCount} of {c.passages.length} passages will be included. Not yet ready: {notReady.map((p) => `#${p.index + 1}`).join(", ")} (generate & approve to add them).</div>)
                          : <div className="text-[11px] text-slate-500">No approved audio yet — generate and approve passages in the Production Desk to include this section.</div>}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        );
      })()}

      {running && (
        <div className="mt-3 rounded-md border border-teal-500/30 bg-teal-500/[0.06] p-3" data-testid="book-job-progress">
          <div className="mb-1.5 flex items-center justify-between text-xs text-teal-200">
            <span className="flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering book{job.current ? ` · ${job.current}` : ""} · {job.done_sections}/{job.total_sections} sections</span>
            <span className="text-[11px] text-slate-400">runs in the background — you can keep working</span>
          </div>
          <Progress value={Math.round((job.done_sections / Math.max(1, job.total_sections)) * 100)} className="h-2" />
        </div>
      )}

      <Dialog open={!!plan} onOpenChange={(o) => !o && setPlan(null)}>
        <DialogContent className="max-w-lg border-teal-500/20 bg-[#12161f] text-slate-100" data-testid="book-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-2xl"><Library className="h-5 w-5 text-teal-400" /> Batch export all chapters</DialogTitle>
            <DialogDescription className="text-slate-400">{plan?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {!plan?.complete && (
              <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-300">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> Draft — some sections are incomplete or skipped
              </div>
            )}
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {(plan?.sections || []).map((s) => (
                <div key={s.chapter_order} className="flex items-center justify-between rounded-md border border-border bg-[#0d1016] px-3 py-1.5 text-xs" data-testid={`book-section-${s.chapter_order}`}>
                  <span className="text-slate-200">{s.title}</span>
                  <span className={`font-mono ${s.included ? (s.final_ready ? "text-emerald-300" : "text-amber-300") : "text-slate-600"}`}>
                    {s.included ? `${s.ready_passages}/${s.total_passages} ready${s.final_ready ? " · complete" : " · partial"}` : "skipped (none ready)"}
                  </span>
                </div>
              ))}
            </div>
            <div className="font-mono text-[11px] text-slate-500">{plan?.included_sections} section(s) · {plan?.total_passages} passages will be combined into one file.</div>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPlan(null)} className="border-border text-slate-300">Cancel</Button>
            <Button data-testid="confirm-book-export" disabled={busy} onClick={render} className="gap-2 bg-teal-500 text-slate-950 hover:bg-teal-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />} Render combined book
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AssemblyWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/assembly`);
    setData(data);
    setSettings((s) => s || data.default_settings);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !settings) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  const chapters = data.chapters || [];
  const upd = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const bookExports = (data.exports || []).filter((e) => e.mode === "book");
  const exportsFor = (chap) => (data.exports || []).filter(
    (e) => e.mode !== "book" && (e.chapter_id ? e.chapter_id === chap.chapter_id : e.chapter_order === chap.chapter_order));

  return (
    <div data-testid="assembly-workspace">
      <button onClick={() => navigate(`/studio/projects/${projectId}`)} className="mb-4 flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-300" data-testid="assembly-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to project
      </button>
      <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 7 · Assembly & Export</div>
      <h1 className="font-serif text-4xl tracking-tight text-slate-100">Master Assembly</h1>
      <p className="mt-1 text-sm text-slate-400">Every chapter — Prologue, Chapter One and beyond — assembles in book order, each exporting separately with its own heading, cue sheet and placed repairs. FFmpeg render only — no ElevenLabs calls.</p>

      {chapters.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border py-20 text-center text-slate-400">No chapters planned yet.</div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Shared export settings */}
          <div className="rounded-lg border border-border bg-[#12161f] p-4" data-testid="assembly-settings">
            <div className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Gauge className="h-3 w-3" /> Export settings (applies to every chapter export)</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <label className="text-xs text-slate-400">Sample rate (Hz)
                <Input data-testid="setting-sample-rate" type="number" value={settings.sample_rate} onChange={(e) => upd("sample_rate", Number(e.target.value))} className="mt-1 bg-[#0d1016]" />
              </label>
              <label className="text-xs text-slate-400">Channels
                <Select value={String(settings.channels)} onValueChange={(v) => upd("channels", Number(v))}>
                  <SelectTrigger data-testid="setting-channels" className="mt-1 bg-[#0d1016]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1">Mono</SelectItem><SelectItem value="2">Stereo</SelectItem></SelectContent>
                </Select>
              </label>
              <label className="text-xs text-slate-400">WAV bit depth
                <Select value={String(settings.wav_bit_depth)} onValueChange={(v) => upd("wav_bit_depth", Number(v))}>
                  <SelectTrigger data-testid="setting-bit-depth" className="mt-1 bg-[#0d1016]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="16">16-bit</SelectItem><SelectItem value="24">24-bit</SelectItem><SelectItem value="32">32-bit</SelectItem></SelectContent>
                </Select>
              </label>
              <label className="text-xs text-slate-400">MP3 bitrate
                <Select value={String(settings.mp3_bitrate)} onValueChange={(v) => upd("mp3_bitrate", v)}>
                  <SelectTrigger data-testid="setting-mp3-bitrate" className="mt-1 bg-[#0d1016]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="128k">128k</SelectItem><SelectItem value="192k">192k</SelectItem><SelectItem value="256k">256k</SelectItem><SelectItem value="320k">320k</SelectItem></SelectContent>
                </Select>
              </label>
              <label className="text-xs text-slate-400">Crossfade (ms)
                <Input data-testid="setting-crossfade" type="number" value={settings.crossfade_ms} onChange={(e) => upd("crossfade_ms", Number(e.target.value))} className="mt-1 bg-[#0d1016]" />
              </label>
            </div>
          </div>
          {!data.ffmpeg_available && <div className="text-xs text-red-300"><AlertTriangle className="mr-1 inline h-3 w-3" />FFmpeg unavailable on the server — export disabled.</div>}

          <BookExportPanel projectId={projectId} ffmpegAvailable={data.ffmpeg_available} onDone={load} initialJob={data.book_job} chapters={chapters} />

          {bookExports.length > 0 && (() => {
            const readyTitles = chapters.filter((c) => c.passages.some((p) => p.ready)).map((c) => c.title);
            return (
            <div data-testid="book-exports-list">
              <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-teal-300/80"><Library className="h-3 w-3" /> Whole-book exports ({bookExports.length})</div>
              <div className="space-y-2">{bookExports.map((e, idx) => {
                const have = new Set((e.sections || []).map((s) => s.title));
                const missing = readyTitles.filter((t) => !have.has(t));
                return <ExportCard key={e.id} projectId={projectId} e={e} isLatestBook={idx === 0}
                  missingSections={missing} displayNumber={bookExports.length - idx}
                  deletable={idx !== 0} onDeleted={load} />;
              })}</div>
            </div>
            );
          })()}

          <Accordion type="multiple" data-testid="assembly-accordion"
            className="space-y-2">
            {chapters.map((chap) => {
              const notReadyCount = chap.passages.filter((p) => !p.ready).length;
              const chExports = exportsFor(chap);
              const val = chap.chapter_id || String(chap.chapter_order);
              return (
                <AccordionItem key={val} value={val} data-testid={`assembly-acc-${chap.chapter_id}`}
                  className="overflow-hidden rounded-xl border border-border/70 bg-[#0b0e14]">
                  <AccordionTrigger data-testid={`assembly-acc-trigger-${chap.chapter_id}`}
                    className="px-4 py-3 hover:no-underline">
                    <div className="flex flex-1 items-center justify-between gap-3 pr-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${KIND_BADGE[chap.kind] || KIND_BADGE.chapter}`}>{chap.kind || "chapter"}</span>
                        <span className="truncate font-serif text-base text-slate-100">{chap.title}</span>
                        <span className="whitespace-nowrap font-mono text-[10px] text-slate-500">{chap.passages.length} psg</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {chExports.length > 0 && (
                          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] text-sky-300 ring-1 ring-sky-500/30" data-testid={`acc-export-badge-${chap.chapter_id}`}>
                            <Download className="h-3 w-3" /> {chExports.length} export{chExports.length > 1 ? "s" : ""}
                          </span>
                        )}
                        {chap.final_ready
                          ? <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>
                          : <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> {chap.passages.length === 0 ? "Empty" : `${notReadyCount}/${chap.passages.length} not ready`}</span>}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0" data-testid={`assembly-acc-content-${chap.chapter_id}`}>
                    <ChapterSection projectId={projectId} chap={chap}
                      settings={settings} exports={exportsFor(chap)} ffmpegAvailable={data.ffmpeg_available} onDone={load} embedded />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
}
